import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  BooleanInput, Create, Edit, SimpleForm, TextInput, Title, TopToolbar, required, useNotify
} from 'react-admin';
import { BackLink } from './back';
import { Link, useNavigate } from 'react-router-dom';
import { callAction, dataProvider } from './dataProvider';
import { FileImage } from './image';
import { FilejetImageInput } from './upload';

/**
 * The people on the contact page, in the order they appear there.
 *
 * Same hand-rolled drag list as the rewards: the order is the whole point of
 * the screen, and react-admin's Datagrid cannot express it.
 *
 * A contact keeps working with no photo and with no name - "OBJEDNÁVKY" is a
 * shared line rather than a person - so only the role is required.
 */

interface StaffMember {
  id: number;
  weight: number;
  role: string;
  name: string;
  phone: string;
  email: string;
  fileIdentifier: string | null;
  imagePath: string | null;
  isVisible: boolean;
}

export function StaffList() {
  const notify = useNotify();
  const [people, setPeople] = useState<StaffMember[] | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<StaffMember | null>(null);

  const load = () => {
    callAction('staff', 'GET' as never)
      .then(value => setPeople((value as { data: StaffMember[] }).data))
      .catch(() => notify('Kontakty sa nepodarilo načítať.', { type: 'warning' }));
  };

  useEffect(load, []);

  const move = (fromId: number, toId: number) => {
    if (people == null || fromId === toId) return;

    const next = [...people];
    const from = next.findIndex(person => person.id === fromId);
    const to = next.findIndex(person => person.id === toId);
    if (from < 0 || to < 0) return;

    const [moved] = next.splice(from, 1);
    if (moved == null) return;
    next.splice(to, 0, moved);
    setPeople(next);
  };

  const persist = async () => {
    if (people == null) return;
    setSaving(true);
    try {
      await callAction('staff/order', 'PUT', { ids: people.map(person => person.id) });
      notify('Poradie uložené.', { type: 'success' });
    } catch {
      notify('Poradie sa nepodarilo uložiť.', { type: 'warning' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (person: StaffMember) => {
    setPendingDelete(null);
    try {
      await dataProvider.delete('staff', { id: person.id, previousData: person });
      notify('Kontakt bol zmazaný.', { type: 'info' });
      load();
    } catch {
      notify('Kontakt sa nepodarilo zmazať.', { type: 'warning' });
    }
  };

  if (people == null) return <Typography sx={{ p: 2 }}>Načítava sa…</Typography>;

  return (
    <Card sx={{ mt: 1 }}>
      <Title title="Kontakty na stránke" />
      <TopToolbar>
        <Button component={Link} to="/staff/create" startIcon={<AddIcon />} size="small">
          Pridať kontakt
        </Button>
      </TopToolbar>
      <CardContent>
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          Toto sú kontakty na stránke Kontakt. Poradie zmeníte ťahaním; uloží sa po pustení.
        </Alert>

        <Stack spacing={0.5}>
          {people.map(person => (
            <Stack
              key={person.id}
              direction="row"
              spacing={2}
              alignItems="center"
              draggable
              onDragStart={() => setDragging(person.id)}
              onDragOver={event => {
                event.preventDefault();
                if (dragging != null) move(dragging, person.id);
              }}
              onDragEnd={() => { setDragging(null); void persist(); }}
              sx={{
                p: 1,
                borderRadius: 1,
                cursor: 'grab',
                opacity: dragging === person.id ? 0.4 : person.isVisible ? 1 : 0.55,
                bgcolor: dragging === person.id ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <DragIndicatorIcon fontSize="small" color="disabled" />
              <Box sx={{ width: 48, height: 48, flexShrink: 0 }}>
                <StaffPhoto person={person} size={48} />
              </Box>
              <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" noWrap>{person.role}</Typography>
                <Typography variant="body2" noWrap>
                  {person.name === '' ? '—' : person.name}
                </Typography>
              </Stack>
              <Stack sx={{ minWidth: 0, display: { xs: 'none', md: 'flex' } }}>
                <Typography variant="caption" noWrap>{person.phone}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{person.email}</Typography>
              </Stack>
              {!person.isVisible && (
                <Tooltip title="Na webe sa nezobrazuje">
                  <VisibilityOffIcon fontSize="small" color="disabled" />
                </Tooltip>
              )}
              <Tooltip title="Upraviť">
                <IconButton component={Link} to={`/staff/${person.id}`} size="small" aria-label="Upraviť kontakt">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zmazať">
                <IconButton size="small" aria-label="Zmazať kontakt" onClick={() => setPendingDelete(person)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>

        {saving && <Typography variant="caption" color="text.secondary">Ukladá sa poradie…</Typography>}
      </CardContent>

      <Dialog open={pendingDelete != null} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Zmazať kontakt?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Kontakt {pendingDelete?.name === '' ? `„${pendingDelete?.role}“` : pendingDelete?.name} zmizne
            zo stránky Kontakt. Ak ho chcete len dočasne skryť, upravte ho a vypnite „Zobrazený“.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Zrušiť</Button>
          <Button color="error" onClick={() => { if (pendingDelete != null) void remove(pendingDelete); }}>
            Zmazať
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

/**
 * The photo, preferring an upload over the asset the page shipped with.
 *
 * Everyone still carries the webpack path they had as Twig, so the list looks
 * like the live page from the first day - but that path is only a filename to
 * this app, not something it can render. It is shown as a caption instead of a
 * broken image, and disappears the moment a real photo is uploaded.
 */
function StaffPhoto({ person, size }: { person: StaffMember; size: number }) {
  if (person.fileIdentifier != null) {
    return <FileImage id={person.fileIdentifier} size={size} alt={person.name} />;
  }

  return (
    <Tooltip title={person.imagePath ?? 'Bez fotografie'}>
      <Box
        sx={{
          width: size,
          height: size,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, textAlign: 'center' }}>
          {person.imagePath == null ? 'bez fotky' : 'pôvodná'}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function StaffFields() {
  return (
    <>
      <TextInput
        source="role"
        label="Zaradenie"
        validate={required()}
        helperText="Nadpis nad kontaktom, napríklad FAKTURÁCIA."
        fullWidth
      />
      <TextInput source="name" label="Meno" fullWidth helperText="Môže zostať prázdne pri spoločnom kontakte." />
      <TextInput source="phone" label="Telefón" fullWidth />
      <TextInput source="email" label="E-mail" fullWidth />
      <BooleanInput source="isVisible" label="Zobrazený na webe" />
      <FilejetImageInput
        source="fileIdentifier"
        label="Fotografia"
        helperText="Štvorcová fotografia vyzerá najlepšie. PNG, JPEG, WebP, GIF alebo AVIF, do 10 MB."
      />
    </>
  );
}

export function StaffEdit() {
  return (
    <Edit title="Kontakt" mutationMode="pessimistic" redirect="list" actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}>
      <SimpleForm>
        <OriginalPhotoNotice />
        <StaffFields />
      </SimpleForm>
    </Edit>
  );
}

function OriginalPhotoNotice() {
  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
      Ak fotografiu nenahráte, zostane pôvodná zo stránky. Nahratá fotografia ju nahradí.
    </Alert>
  );
}

export function StaffCreate() {
  const navigate = useNavigate();

  return (
    <Create
      title="Nový kontakt"
      mutationOptions={{ onSuccess: () => navigate('/staff') }}
    >
      <SimpleForm defaultValues={{ isVisible: true, name: '', phone: '', email: '' }}>
        <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
          Nový kontakt sa zaradí na koniec zoznamu. Poradie potom upravíte ťahaním.
        </Alert>
        <StaffFields />
      </SimpleForm>
    </Create>
  );
}
